<template>
  <div>
    <v-btn
      class="mx-2"
      small
      color="primary"
      @click="editSelectedImages"
      :disabled="!selectedImages.length > 0"
      outlined
      >{{ $t('common.done') }}</v-btn
    >

    <v-dialog v-model="dialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h5">
          {{ title }}
        </v-card-title>

        <v-card-text v-if="!loading && editionTypeSelected === 'CROPPING' && results.length > 0" class="pa-5">
          <p class="mb-5 text-center">{{ message }}</p>
          <div class="pa-2 d-flex align-center" v-for="(result, i) in results" :key="i">
            <img width="150" :src="result.image.image.url" alt="" />
            <p class="ml-3">{{ resultCroppingMesage(result) }}</p>
          </div>
        </v-card-text>

        <v-card-text
          v-else-if="!loading && editionTypeSelected === 'LICENSE_PLATE_COVER' && results.length > 0"
          class="pa-5"
        >
          <p class="mb-5 text-center">{{ message }}</p>
          <div class="pa-2 d-flex align-center" v-for="(result, i) in results" :key="i">
            <img width="150" :src="result.image.url" alt="" />
            <p class="ml-3">{{ resultLicensePlateCoverMesage(result) }}</p>
          </div>
        </v-card-text>

        <v-card-text v-else class="pa-5">
          <p class="mb-2 text-center">{{ message }}</p>
          <div class="d-flex justify-center">
            <v-progress-circular v-if="loading" indeterminate color="primary"></v-progress-circular>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="loading" color="primary" text @click="closeModal">
            {{ $t('common.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  props: {
    details: Boolean
  },

  data() {
    return {
      dialog: false,
      loading: false,
      message: '',
      results: []
    }
  },

  unmounted() {
    this.resetData()
  },

  computed: {
    ...mapState({
      selectedImages: (state) => state.editShooting.selectedImages,
      shooting: (state) => state.shooting,
      editionTypeSelected: (state) => state.editShooting.editionTypeSelected,
      userId: (state) => state.user.preferred_username
    }),
    shootingId() {
      return this.$route.params.shootingId
    },
    title() {
      let convertedTypeToText

      const croppedTextTitle = this.$t('common.croppedTextTitle')
      const licenseTextTitle = this.$t('common.licenseTextTitle')

      this.editionTypeSelected === 'CROPPING'
        ? (convertedTypeToText = croppedTextTitle)
        : (convertedTypeToText = licenseTextTitle)

      return convertedTypeToText
    }
  },

  methods: {
    resultLicensePlateCoverMesage(result) {
      if (result.applyPositionResponse.code === 100) {
        return this.$t('common.errorApplyLicensePlateCover')
      } else if (!result.applyPositionResponse.error) {
        return this.$t('common.validApplyLicensePlateCover')
      } else {
        return this.$t('common.commonErrorMessage')
      }
    },
    resultCroppingMesage(result) {
      if (!result.croppingResult || result.croppingResult.code === 200) {
        return this.$t('common.errorApplyCropping')
      } else if (!result.croppingResult.error) {
        return this.$t('common.applyCroppingDone')
      } else {
        return this.$t('common.commonErrorMessage')
      }
    },
    getOriginalImages(selectedImages, shooting) {
      let originalImages

      originalImages = selectedImages.map((e) => {
        return shooting.find((n) => {
          return e.order === n.order && n.imageType === 'ORIGINAL'
        })
      })

      return originalImages
    },
    resetData() {
      // Reset screen status
      this.$store.commit('UPDATE_SHOOTING_EDITION_MODE', {
        editionTypeSelected: null,
        isEditMode: false,
        selectedImages: []
      })
    },

    closeModal() {
      // Reset screen status
      this.resetData()
      this.dialog = false
      location.reload()
    },

    async editSelectedImages() {
      this.dialog = true
      this.loading = true

      /*
       * Selection des images ORIGINAL depuis la liste d'images séléctionnées
       * Récupère la configuration de stamp de chacune des images
       * Crop la liste des images (Via Falyt)
       * */

      // Find ORIGINAL images from selection
      const originalImages = this.getOriginalImages(this.selectedImages, this.shooting)

      // Get stamp information
      this.message = this.$t('common.getConfigurations')
      const originalImagesWithStampConfiguration = await this.$store.dispatch('getStampConfiguration', originalImages)

      if (this.editionTypeSelected === 'CROPPING') {
        await this.croppedImages(originalImagesWithStampConfiguration)
      } else if (this.editionTypeSelected === 'LICENSE_PLATE_COVER') {
        await this.licensePlateCoverImages(originalImagesWithStampConfiguration)
      }
    },

    async croppedImages(originalImagesWithStampConfiguration) {
      this.message = this.$t('common.croppingInProgress')

      // First step : crop images
      const croppedImagesSelected = await this.$store.dispatch(
        'croppedImagesSelected',
        originalImagesWithStampConfiguration
      )

      // Second step : stamp cropped images
      this.message = this.$t('common.shootingUpdate')
      const stampedImages = await this.$store.dispatch('stampImages', croppedImagesSelected)

      // Third step : set new shooting cropped and stamped
      const newShooting = await this.$store.dispatch('addImagesShooting', {
        shootingId: this.shootingId,
        imageInformations: stampedImages
      })

      // Fourth step : send advalorem verification
      await this.$store.dispatch('advaloremVerification', {
        data: newShooting,
        shootingId: this.shootingId,
        userId: this.userId,
        type: 'CROPPING'
      })

      this.message = this.$t('common.endOfProcess')
      if (newShooting) {
        this.results = newShooting.imageInformations?.images || []
        this.loading = false
      }
    },

    async licensePlateCoverImages(imagesWithStampConfiguration) {
      //start api call
      this.message = this.$t('common.getPosition')

      const imagesWithLicensePlatePosition = await this.$store.dispatch(
        'getLicensePlateCoverForImage',
        imagesWithStampConfiguration
      )

      this.message = this.$t('common.applyLicensePlateCover')
      const imagesWithLicensePLateCoverApply = await this.$store.dispatch('applyLicensePlateCoverPositionOnImage', {
        imagesWithLicensePlatePosition,
        shootingId: this.shootingId
      })

      if (imagesWithLicensePLateCoverApply[0].applyPositionResponse.isError) {
        this.message = this.$t('common.errorApplyLicensePlateCover')
        this.loading = false
        return
      }

      // Send advalorem verification
      await this.$store.dispatch('advaloremVerification', {
        data: imagesWithLicensePLateCoverApply,
        shootingId: this.shootingId,
        userId: this.userId,
        type: 'LICENCE_PLATE_COVER'
      })

      //Fin du chargement
      this.message = this.$t('common.endOfProcess')
      if (imagesWithLicensePLateCoverApply) this.loading = false

      //display result in modal
      this.results = imagesWithLicensePLateCoverApply
    }
  }
}
</script>
